import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * BackupCodeEnrollStep — the screen the user sees when the pipeline runtime
 * pauses at `credential_set` (or `mfa_enroll`) with `factor: "backup_code"`
 * during ENROLLMENT. The freshly-generated single-use recovery codes are
 * shown ONCE; the user copies / saves them and acknowledges before the
 * pipeline continues. They are never shown again.
 *
 * Distinct from BackupCodeVerifyStep (which collects a code at login). This
 * step DISPLAYS the codes and has a single "I've saved my codes" action —
 * there is no text input.
 *
 * Composition: heading + helper copy + a mono grid of the codes + a
 * "warning, shown once" note + Copy-all action + acknowledge Button. No
 * network calls — `onAcknowledge` comes from the parent.
 *
 * A11y:
 *   - The codes list is a real <ul> so screen readers enumerate each code.
 *   - Copy feedback is announced via an aria-live region.
 *   - The acknowledge Button is the only primary action.
 */

export interface BackupCodeEnrollStepProps {
  /** The generated recovery codes to display. */
  codes: string[]
  /** Called when the user confirms they have saved the codes. */
  onAcknowledge: () => void | Promise<void>
  /** Set true while the acknowledge resume is in flight. */
  busy?: boolean
  /** Inline error surfaced by the parent. */
  error?: string
  /** Override the visible heading. Default: "Save your backup codes". */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /** Extra classes for the wrapper. */
  className?: string
}

export const BackupCodeEnrollStep = React.forwardRef<HTMLDivElement, BackupCodeEnrollStepProps>(
  (
    {
      codes,
      onAcknowledge,
      busy = false,
      error,
      heading = 'Save your backup codes',
      helperText = 'Each code works once if you lose access to your other methods. Store them somewhere safe — they won’t be shown again.',
      className,
    },
    ref,
  ) => {
    const [copied, setCopied] = React.useState(false)

    const handleCopy = React.useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codes.join('\n'))
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      } catch {
        // Clipboard can be unavailable (insecure context / permissions).
        // The codes are visible on screen regardless; no hard failure.
      }
    }, [codes])

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)}>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="text-heading-sm font-semibold text-ia-text">{heading}</h2>
          {helperText ? (
            <p className="text-body-sm text-ia-text-secondary">{helperText}</p>
          ) : null}
        </div>

        <ul
          className="grid grid-cols-2 gap-2 rounded-md border border-ia-border bg-ia-elevated p-3"
          data-testid="backup-codes-list"
        >
          {codes.map((code) => (
            <li
              key={code}
              className="font-mono text-body-sm tabular-nums text-ia-text text-center select-all"
            >
              {code}
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={handleCopy} disabled={busy}>
            {copied ? 'Copied' : 'Copy codes'}
          </Button>
          <span aria-live="polite" className="text-body-sm text-ia-text-secondary">
            {copied ? 'Codes copied to clipboard' : ''}
          </span>
        </div>

        {/* Reserved error row so layout doesn't jump in/out. */}
        <div
          role="alert"
          aria-live="polite"
          className={cn(
            'min-h-5 text-center text-body-sm',
            error ? 'text-ia-error' : 'text-transparent',
          )}
        >
          {error || 'placeholder'}
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() => {
            void onAcknowledge()
          }}
          loading={busy}
          disabled={busy}
          className="w-full"
        >
          {busy ? 'Continuing…' : "I've saved my codes"}
        </Button>
      </div>
    )
  },
)
BackupCodeEnrollStep.displayName = 'BackupCodeEnrollStep'
